import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './i18n/config';
import './styles/global.scss';
import { kernelInit, LGInit } from './utils/projectInit';
import { initPlayerController } from './utils/playerController';
import { initPlayerConfig } from './utils/initPlayerConfig';
import { initAppConfig } from './utils/initAppConfig';
import { initEasterEggCheck } from './utils/easterEggAuth';
import { initPlayerHistoryManager } from './utils/playerHistoryManager';
import { initAegis } from './utils/aegisInit';

// ==================== 扩展 Window 接口 ====================
declare global {
  interface Window {
    proWujICfgInfo?: Record<string, any>;
    __WETV_CONFIG__?: Record<string, any>;
  }
}

// ==================== 全局环境初始化 ====================
// 初始化全局配置对象，防止 undefined 访问
if (typeof window !== 'undefined') {
  // 为可能缺失的全局对象设置默认值
  window.proWujICfgInfo = window.proWujICfgInfo || {};
  (window as any).__WETV_CONFIG__ = (window as any).__WETV_CONFIG__ || {};
}

// ==================== 全局错误处理（必须放在最前面） ====================
// 捕获 wetv-kernel 和 wetv-report-sdk 内部的错误，避免影响应用运行
if (typeof window !== 'undefined') {
  // 清理 localStorage 中的 Aegis 上报缓存，避免配额超出
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('localCacheReport') || key.includes('aegis') || key.includes('reportCache'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log('🧹 已清理 Aegis 上报缓存:', keysToRemove.length, '项');
    }
  } catch (error) {
    console.warn('⚠️ 清理 localStorage 缓存失败:', error);
  }

  // 检查是否是 pageParams 相关错误
  function isPageParamsRelatedError(message: unknown, error?: { message?: string; stack?: string }): boolean {
    return (
      (typeof message === 'string' && message.includes('pageParams')) ||
      Boolean(error?.message?.includes('pageParams')) ||
      Boolean(error?.stack?.includes('pageParams'))
    );
  }

  // 捕获未处理的 Promise 拒绝
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (
      error &&
      (error.message?.includes('Network Error') ||
        error.message?.includes('AxiosError') ||
        error.message?.includes('QuotaExceededError') ||
        error.message?.includes('setItem') ||
        error.message?.includes('pageParams') ||
        error.stack?.includes('wetv.kernel'))
    ) {
      console.warn('⚠️ 内部错误（已忽略）:', error.message || error);
      event.preventDefault();
    }
  });

  // 捕获全局同步错误（包括 TypeError）
  const originalErrorHandler = window.onerror;
  window.onerror = function (message, source, lineno, colno, error) {
    if (isPageParamsRelatedError(message, error)) {
      console.warn('⚠️ pageParams 错误（已忽略）:', message);
      return true; // 返回 true 阻止默认错误处理
    }

    // 其他错误交给原有处理器
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    return false;
  };

  // 额外的 error 事件监听器作为备份
  window.addEventListener(
    'error',
    (event) => {
      const { error, message } = event;
      if (isPageParamsRelatedError(message, error)) {
        console.warn('⚠️ pageParams 错误（addEventListener 捕获）:', error?.message || message);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    },
    true
  ); // 使用捕获阶段
}

// ==================== 应用初始化 ====================

// 初始化应用配置（从 URL 参数获取渠道号和产品线信息）
initAppConfig();

// 初始化播放器配置（从 URL 参数获取环境信息和版本信息）
initPlayerConfig();

// 初始化 wetv-kernel（必须在应用启动前初始化）
kernelInit();

// 初始化 Aegis 上报
initAegis();

// 初始化 LG webOS 平台（注册 webOSRelaunch 监听器）
LGInit();

// 初始化播放器控制器（暴露到全局 window 对象）
initPlayerController();

// 初始化播放历史记录管理器（在应用启动时初始化，监听播放器事件）
initPlayerHistoryManager();

// 初始化彩蛋页面权限检查（异步，不阻塞应用启动）
initEasterEggCheck();

const container = document.getElementById('root');

if (!container) {
  throw new Error('根元素 #root 未找到');
}

const root = createRoot(container);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

console.log('🚀 WeTV React SPA 已启动');
console.log('📦 环境:', process.env.NODE_ENV);
